import { ConfigServiceOptions } from '../../origami-api/services/config.service';
import ModuleName = ConfigServiceOptions.ModuleName;
import { SecurityModule } from './modules/security';
import { EmailModule } from './modules/email';
import { ReportsModule } from './modules/reports';
import { GeneralModule } from './modules/general';
import { QuestionModule } from './modules/questions';
import { NotificationsModule } from './modules/notifications';
import { ConfidentialityAgreementModule } from './modules/confidentiality-agreement';
import { InvestigativeSitesModule } from './modules/investigative-site';
import { CountryInDataRoomModule } from './modules/dataroom-country';
import { WorkflowModule } from './modules/workflow';
import { DocumentTypesModule } from './modules/document-type';
import { ProgramModule } from './modules/program';
import { SimpleTopic } from '../../origami-api/utils/topic-utils';
import { createSettingsDecorator, BaseSettingsGroup } from './utils/setting-group-base';
import { SettingsModule } from './utils/settings-module';
import { Type } from '@angular/core';
import { StringHash } from '@app/shared/utils/utils-types';
import { SettingsStore } from './utils/settings-store';
import { CustomSettingsModule } from './utils/settings-module-custom';
import { Observable, of } from 'rxjs';
import { DocumentViewModule } from '@app/services/settings/modules/document-view';
import { UiConfigModule } from './modules/ui-config';
import { ManagerAvailableModulesModule } from '@app/services/settings/modules/manager-available-modules';
import { TranslationsModule } from '@app/services/settings/modules/translations';
import { OrganizationsModule } from '@app/services/settings/modules/organizations';
import { InstitutionalReviewBoardModule } from '@app/services/settings/modules/institutional-review-board';
import { IncidentTypesModule } from '@app/services/settings/modules/incident-types';
import { CapaTypesModule } from '@app/services/settings/modules/capa-types';
import { ActionItemTypesModule } from '@app/services/settings/modules/action-item-types';
import { LabelsModule } from '@app/services/settings/modules/labels';
import { QmsAuditTypesModule } from '@app/services/settings/modules/qms-audit-types';
import { QmsFindingTypesModule } from '@app/services/settings/modules/qms-finding-types';
import { QmsSupplierQualificationTypesModule } from '@app/services/settings/modules/qms-supplier-qualification-types';

export class MainSettingsModule extends BaseSettingsGroup<SettingsStore, SimpleTopic[]> {
  @MainSettingsModule.value(ModuleName.Security, SecurityModule) security: SecurityModule;
  @MainSettingsModule.value(ModuleName.Email, EmailModule) email: EmailModule;
  @MainSettingsModule.value(ModuleName.Reports, ReportsModule) reports: ReportsModule;
  @MainSettingsModule.value(ModuleName.General, GeneralModule) general: GeneralModule;
  @MainSettingsModule.value(ModuleName.Question, QuestionModule) question: QuestionModule;
  @MainSettingsModule.value(ModuleName.Notifications, NotificationsModule) notifications: NotificationsModule;
  @MainSettingsModule.value(ModuleName.ConfidentialityAgreement, ConfidentialityAgreementModule)
  confidentialityAgreement: ConfidentialityAgreementModule;
  @MainSettingsModule.value(ModuleName.InvestigativeSites, InvestigativeSitesModule)
  investigativeSites: InvestigativeSitesModule;
  @MainSettingsModule.value(ModuleName.CountryInDataRoom, CountryInDataRoomModule)
  countryInDataRoom: CountryInDataRoomModule;
  @MainSettingsModule.value(ModuleName.InstitutionalReviewBoard, InstitutionalReviewBoardModule)
  institutionalReviewBoard: InstitutionalReviewBoardModule;
  @MainSettingsModule.value(ModuleName.Organizations, OrganizationsModule)
  organizations: OrganizationsModule;
  @MainSettingsModule.value(ModuleName.Workflow, WorkflowModule) workflow: WorkflowModule;
  @MainSettingsModule.value(ModuleName.DocumentTypes, DocumentTypesModule) documentTypes: DocumentTypesModule;
  @MainSettingsModule.value(ModuleName.IncidentTypes, IncidentTypesModule) incidentTypes: IncidentTypesModule;
  @MainSettingsModule.value(ModuleName.CapaTypes, CapaTypesModule) capaTypes: CapaTypesModule;
  @MainSettingsModule.value(ModuleName.ActionItemTypes, ActionItemTypesModule) actionItemTypes: ActionItemTypesModule;
  @MainSettingsModule.value(ModuleName.Program, ProgramModule) program: ProgramModule;
  @MainSettingsModule.value(ModuleName.DocumentView, DocumentViewModule) documentView: DocumentViewModule;
  @MainSettingsModule.value(ModuleName.UiConfiguration, UiConfigModule) uiConfig: UiConfigModule;
  @MainSettingsModule.value(ModuleName.ManagerAvailableModules, ManagerAvailableModulesModule)
  managerAvailableModules: ManagerAvailableModulesModule;
  @MainSettingsModule.value(ModuleName.Translations, TranslationsModule) translations: TranslationsModule;
  @MainSettingsModule.value(ModuleName.Labels, LabelsModule) labels: LabelsModule;
  @MainSettingsModule.value(ModuleName.AuditTypes, QmsAuditTypesModule) auditTypes: QmsAuditTypesModule;
  @MainSettingsModule.value(ModuleName.FindingTypes, QmsFindingTypesModule) findingTypes: QmsFindingTypesModule;
  @MainSettingsModule.value(ModuleName.SupplierTypes, QmsSupplierQualificationTypesModule)
  supplierTypes: QmsSupplierQualificationTypesModule;

  private customModules: StringHash<CustomSettingsModule> = {};

  private static value(moduleName: ModuleName, constructor: Type<SettingsModule>) {
    return createSettingsDecorator(function () {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const _this: MainSettingsModule = this;
      return new constructor(moduleName, _this.mainStore, _this.defaultStore, (topic) => _this.saveData([topic]));
    });
  }

  any(moduleName: ModuleName): CustomSettingsModule {
    if (!this.customModules[moduleName]) {
      this.customModules[moduleName] = new CustomSettingsModule(
        moduleName,
        this.mainStore,
        this.defaultStore,
        (topic) => this.saveData([topic]),
      );
    }
    return this.customModules[moduleName];
  }

  getValues(): StringHash<any> {
    const result = {};

    this.keys.forEach((key) => {
      const item: SettingsModule = this[key];
      result[this[key].moduleName] = item.getValues();
    });

    Object.keys(this.customModules).forEach((moduleName) => {
      if (!result[moduleName]) {
        result[moduleName] = this.customModules[moduleName].getValues();
      }
    });

    return result;
  }

  getDbValues(modules?: ModuleName[], returnOnlyDirty: boolean = true): SimpleTopic[] {
    const alreadyAdded = {};
    modules = modules || this.keys.map((key) => this[key].moduleName).concat(Object.keys(this.customModules));

    return modules
      .map((moduleName) => {
        if (!alreadyAdded[moduleName]) {
          alreadyAdded[moduleName] = true;

          const moduleKey = this.keys.find((key) => this[key].moduleName === moduleName);
          if (moduleKey) {
            return this[moduleKey].getDbValues(returnOnlyDirty);
          }
          if (this.customModules[moduleName]) {
            return this.customModules[moduleName].getDbValues(returnOnlyDirty);
          }
        }

        return null;
      })
      .filter((v) => v);
  }

  save(modules?: ModuleName[]): Observable<boolean> {
    const topics = this.getDbValues(modules);
    if (topics.length) {
      return this.saveData(topics);
    }
    return of(false);
  }
}
